import React, { useState, useEffect, useRef } from 'react';

import { Row, Col } from 'antd';
import { Field, Formik } from 'formik';

import isUrlHttp from 'is-url-http';
import styled from 'styled-components';

import { socialTypes } from '@constants/Socials';

import { useAddContactSocial, useEditContactSocial } from '@hooks/API/Contact/UseContactSocials';
import useFormFieldFocus from '@hooks/Utils/UseFormFieldFocus';

import { NwButton, NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import { NWInput, NWInputNumber } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import NwIcon from '@components/Gui/NwIcon';
import { buildUrl, parseUrl, isUrl, cleanUsername, ensureHttps } from '@components/Social/SocialUtil';

import SocialIcon from './SocialIcon';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const SocialFormContainer = styled.div`
    .social-types {
        .social-type {
        }
    }

    .social-textbox {
        padding-top: 1rem;

        .ant-input-group-addon {
            background-color: ${(props) => props.theme.LighterBorderColor};
        }

        .social-link-test {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-top: 0.15rem;
            padding-bottom: 4rem;
        }
    }
`;

const SocialForm = ({ contactID, contactType, selectedSocial, onClose }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeSocialType, setActiveSocialType] = useState('');
    const [assembledUrl, setAssembledUrl] = useState('');
    const [addOnBefore, setAddOnBefore] = useState();
    const [inputValue, setInputValue] = useState('');
    const [touchForm, setTouchForm] = useState(false);
    const [shouldFocusInput, setShouldFocusInput] = useState(false);

    const socialTextFieldRef = useRef(null);

    const { mutateAsync: editContactSocial } = useEditContactSocial();
    const { mutateAsync: addContactSocial } = useAddContactSocial();

    useFormFieldFocus('SocialTextField');

    useEffect(() => {
        if (selectedSocial) {
            setInputValue(selectedSocial.Url);
            const socialType = socialTypes.find((s) => s.code === selectedSocial.Type);
            setActiveSocialType(socialType);
            setAssembledUrl(buildUrl(socialType, selectedSocial.Url, contactType));
        } else {
            setActiveSocialType();
            setAssembledUrl('');
        }
    }, [selectedSocial]);

    useEffect(() => {
        if (activeSocialType) {
            setAssembledUrl(buildUrl(activeSocialType, inputValue, contactType));
        } else {
            setAssembledUrl(inputValue);
        }
        setAddOns();
    }, [activeSocialType, inputValue]);

    useEffect(() => {
        if (shouldFocusInput && socialTextFieldRef.current) {
            socialTextFieldRef.current.focus();
            setShouldFocusInput(false);
        }
    }, [shouldFocusInput, activeSocialType]);

    const isValidUrl = (urlString) => {
        return isUrlHttp(urlString);
    };

    const clearActiveSocialType = () => {
        setActiveSocialType();
    };

    const setAddOns = () => {
        if (activeSocialType) {
            setAddOnBefore(
                <>
                    {!selectedSocial && (
                        <>
                            <NwIcon icon={byPrefixAndName.fass['circle-xmark']} onClick={clearActiveSocialType} />
                            &nbsp;
                        </>
                    )}
                    {activeSocialType.prefix}
                </>
            );
        } else {
            setAddOnBefore();
        }
    };

    const handleClickSocial = (clickedSocial, setFieldValue, values) => {
        setActiveSocialType(clickedSocial);
        setShouldFocusInput(true);
    };

    const onChange = (value, setFieldValue) => {
        if (value === '') {
            setInputValue('');
        } else if (isUrl(value)) {
            const { socialType, username } = parseUrl(value);
            if (socialType) {
                setActiveSocialType(socialType);
                setFieldValue('SocialText', username);
                setInputValue(username);
            } else {
                setInputValue(value);
            }
        } else {
            const cleaned = cleanUsername(value);
            setFieldValue('SocialText', cleaned);
            setInputValue(cleaned);
        }
    };

    const afterSave = () => {
        setActiveSocialType();
        setIsSubmitting(false);
        onClose();
    };

    const saveSocial = async (values) => {
        let username = encodeURI(values.SocialText.trim());
        if (username) {
            if (!selectedSocial) {
                let socialTypeCode = 'Other';
                if (activeSocialType) {
                    socialTypeCode = activeSocialType.code;
                } else {
                    username = ensureHttps(username);
                }
                setIsSubmitting(true);
                const data = {
                    Type: socialTypeCode,
                    Url: username,
                    ContactID: contactID,
                    Followers: values.Followers,
                };
                try {
                    await addContactSocial({ data: data, ContactID: contactID });
                    afterSave();
                    setIsSubmitting(false);
                } catch (error) {
                    console.error('Add Social Error => ', error);
                    setIsSubmitting(false);
                }
            } else {
                if (selectedSocial.Url === username && selectedSocial.Followers === values.Followers) {
                    afterSave();
                } else {
                    const data = {
                        ID: selectedSocial.ID,
                        Type: activeSocialType.code,
                        Url: username,
                        ContactID: contactID,
                        Followers: values.Followers,
                    };
                    setIsSubmitting(true);
                    try {
                        await editContactSocial({ data: data, ContactID: contactID });
                        afterSave();
                        setIsSubmitting(false);
                    } catch (error) {
                        console.error('Update Social Error => ', error);
                        setIsSubmitting(false);
                    }
                }
            }
        }
    };

    const initialValues = {
        SocialText: selectedSocial ? selectedSocial.Url : '',
        Followers: selectedSocial ? selectedSocial.Followers : 0,
    };

    const onFormSubmit = (values, setSubmitting) => {
        saveSocial(values);
        setSubmitting(false);
    };

    const navigateToSocial = () => {
        window.open(ensureHttps(assembledUrl), '_blank');
    };

    return (
        <SocialFormContainer>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }) => {
                    onFormSubmit(values, setSubmitting);
                }}
            >
                {({ isSubmitting, values, setFieldValue, handleSubmit, touched, isValid, errors }) => (
                    <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Row>
                            {!selectedSocial && (
                                <Col span={8}>
                                    <div className="social-types">
                                        {socialTypes.map((item) => (
                                            <SocialIcon
                                                key={item.code}
                                                social={item}
                                                active={activeSocialType && activeSocialType.code === item.code}
                                                onClick={handleClickSocial}
                                                setFieldValue={setFieldValue}
                                                values={values}
                                                size="small"
                                                showName
                                            />
                                        ))}
                                    </div>
                                </Col>
                            )}
                            <Col span={selectedSocial ? 24 : 16}>
                                <div className="social-textbox">
                                    <Field
                                        component={NWInput}
                                        addonBefore={addOnBefore}
                                        label=""
                                        name="SocialText"
                                        type="text"
                                        style={{ width: '100%' }}
                                        value={values.SocialText}
                                        onAfterChange={(value) => onChange(value, setFieldValue)}
                                        innerRef={socialTextFieldRef}
                                    />
                                    <div className="social-link-test">
                                        <div>
                                            {selectedSocial && activeSocialType && (
                                                <SocialIcon social={activeSocialType} showName />
                                            )}
                                        </div>
                                        {isValidUrl(assembledUrl) ? (
                                            <NwButton
                                                label="test link"
                                                icon={byPrefixAndName.fasl['link']}
                                                primary
                                                onClick={navigateToSocial}
                                            />
                                        ) : (
                                            assembledUrl && (
                                                <NwButton
                                                    label="not valid url"
                                                    icon={byPrefixAndName.fasl['link-slash']}
                                                    disabled
                                                />
                                            )
                                        )}
                                    </div>
                                    <Field
                                        component={NWInputNumber}
                                        label="Followers (as a number)"
                                        name="Followers"
                                        precision={0}
                                        style={{ width: '100%' }}
                                        value={values.Followers}
                                    />
                                    <NwFormButtonsBar
                                        left={<NwCancelButton disabled={isSubmitting} onClick={() => onClose()} />}
                                        right={
                                            <NwSaveButton
                                                htmlType="submit"
                                                disabled={isSubmitting || !isValidUrl(assembledUrl)}
                                                loading={isSubmitting}
                                            />
                                        }
                                    />
                                </div>
                            </Col>
                        </Row>
                    </NwForm>
                )}
            </Formik>
        </SocialFormContainer>
    );
};

export default SocialForm;
